import React, {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Header } from 'components/Header'
import 'styles/index.css'
import 'styles/tailwind.css'
import 'styles/header.css'
import 'styles/footer.css'
import { Footer } from 'components/Footer'
import { useAutocomplete } from '@shared/universal/components/SearchBar/hooks'
import { SearchBar } from '@shared/universal/components/SearchBar'
import { bracketsToHtml } from '@shared/universal/utils/stringUtils'
import { useDateInput } from '@shared/universal/components/DateInput/hooks'
import { usePersonPicker } from '@shared/universal/components/PersonPicker/hooks'
import {
  buildSearchUrl,
  SearchParams,
} from '@shared/universal/components/SearchBar/utils'
import {
  selectCurrency,
  selectDomainConfig,
  selectLocale,
  selectSliderTitle,
} from 'stores/selectors'
import { IntlContextProvider } from 'components/Intl/IntlContextProvider'
import { useAnalytics } from 'components/hooks/useAnalytics'
import * as t from 'io-ts'
import { AutoCompleteItem } from '@shared/universal/components/SearchBar/decoders/AutoCompleteDecoder'

import { Usp } from './components/Usp'
import { SpainMap } from './components/SpainMap'
import { InspirationCards } from './components/InspirationCards'
import { PopularDestinations } from './components/LinkLists/PopularDestinations'
import { ListWithUsLink } from './components/ListWithUsLink'
import { AboutUs } from './components/AboutUs'
import { Head } from './components/Head'

export const App = () => {
  const [isSearchFieldFocused, setIsSearchFieldFocused] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const sliderTitle = selectSliderTitle()
  const locale = selectLocale()
  const currency = selectCurrency()
  const domainConfig = selectDomainConfig()
  const analytics = useAnalytics()

  useEffect(() => {
    const url = new URL(window.location.href)
    analytics?.trackInternalV4Event({
      url,
      eventAction: 'HOME_PAGE',
      eventCategory: 'PAGEVIEW',
      eventType: 'page',
    })()
    analytics?.trackInternalV5Event({
      url,
      pageType: 'HOME_PAGE',
      eventCategory: 'PAGEVIEW',
      eventType: 'page',
    })()
    analytics?.trackGoogleAnalytics('send', 'pageview')
  }, [analytics])

  const { items, hasGoogleResults } = useAutocomplete(
    searchTerm,
    locale,
    currency,
    domainConfig.id,
  )

  const dateInputHook = useDateInput({
    handleDateSelection: (checkin, checkout) => {
      if (searchTerm.trim()) {
        doSearch({ checkin, checkout })
      }
    },
  })
  const personPickerHook = usePersonPicker()

  const handleSearch = (event?: MouseEvent | FormEvent) => {
    event?.preventDefault()
    doSearch({})
  }

  const doSearch = (params: SearchParams) => {
    window.location.href = buildSearchUrl({
      searchTerm,
      checkin: dateInputHook.checkinDay,
      checkout: dateInputHook.checkoutDay,
      adults: personPickerHook.adults,
      bedrooms: personPickerHook.bedrooms,
      kids: personPickerHook.kids,
      kidsAges: personPickerHook.kidsAges,
      ...params,
    })
  }

  const handleSearchFieldFocus = () => setIsSearchFieldFocused(true)
  const handleSearchFieldBlur = () => setIsSearchFieldFocused(false)
  const handleAutocompleteItemClick = (
    item: t.TypeOf<typeof AutoCompleteItem>,
  ) => {
    if (dateInputHook.checkinDay && dateInputHook.checkoutDay) {
      doSearch({ searchTerm: item.value })
    } else {
      dateInputHook.removeEventListeners()
      setIsSearchFieldFocused(false)
      dateInputHook.handleCheckinInputClick()
    }
  }

  const handleSearchTermChange = useCallback<(value: string) => void>(
    value => setSearchTerm(value),
    [setSearchTerm],
  )

  const handleSearchTermClear = useCallback(
    () => setSearchTerm(''),
    [setSearchTerm],
  )

  return (
    <IntlContextProvider>
      <Head />
      <Header />
      <section className='relative h-home-section-mobile md:h-home-section'>
        <div className='absolute left-0 bottom-0 md:bottom-0 top-0 right-0 flex bg-grey-light'>
          <video className='w-full h-full object-cover' loop muted autoPlay>
            <source
              src={`${process.env.PUBLIC_PATH}assets/videos/frontpage-video.mp4`}
              type='video/mp4'
            />
          </video>
          <div className='absolute z-search-bar left-0 bottom-0 top-0 right-0 pt-32 md:pt-44'>
            <div className='w-full h-full flex flex-col items-stretch'>
              <span
                className='hidden text-7xl md:inline text-center text-white home-title mb-5'
                dangerouslySetInnerHTML={{
                  __html: bracketsToHtml(sliderTitle),
                }}
              />
              <SearchBar
                isSearchFieldFocused={isSearchFieldFocused}
                handleSearchFieldBlur={handleSearchFieldBlur}
                handleSearchFieldFocus={handleSearchFieldFocus}
                personPickerProps={personPickerHook}
                dateInputProps={dateInputHook}
                value={searchTerm}
                hasGoogleResults={hasGoogleResults}
                autocompleteItems={items}
                onAutocompleteItemClick={handleAutocompleteItemClick}
                onChange={handleSearchTermChange}
                onClear={handleSearchTermClear}
                onSubmit={handleSearch}
                onSearchButtonClick={handleSearch}
              />
            </div>
          </div>
        </div>
      </section>
      <Usp />
      <SpainMap />
      <InspirationCards />
      <PopularDestinations />
      <ListWithUsLink />
      <AboutUs />
      <Footer />
    </IntlContextProvider>
  )
}
